import React, { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "src/components/ui/button";
import BorderedSelect from "src/components/form-new/bordered-select";
import {
  useGetCommissionInvoicesQuery,
  useGetCommissionReferralsQuery,
  useGetCommissionUsersQuery,
  useGetCommissionReportsQuery,
  useGetCommissionPnlQuery,
} from "../endpoints/commissions.endpoints";
import type { CommissionReport, GrossMarginRow, Referral } from "../types/commissions.types";
import { formatCurrency } from "../../utils/billing-utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";

const PERIOD_TYPE_OPTIONS = [
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const month = i + 1;
  const label = new Date(2000, i, 1).toLocaleString("default", { month: "long" });
  return { value: String(month), label };
});

function getQuartersForPeriod(): { label: string; year: number; quarter: number }[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentQuarter = Math.ceil(currentMonth / 3);
  const quarters: { label: string; year: number; quarter: number }[] = [];
  for (let i = -2; i <= 1; i++) {
    const total = currentYear * 4 + currentQuarter + i;
    const year = Math.floor((total - 1) / 4);
    const quarter = ((total - 1) % 4) + 1;
    quarters.push({ label: `Q${quarter} ${year}`, year, quarter });
  }
  return quarters;
}

function getYearsForPeriod(): number[] {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1];
}

const QUARTERS = getQuartersForPeriod();
const YEARS = getYearsForPeriod();

function getReportParamsForPeriod(
  periodType: "month" | "quarter",
  selectedMonth: string,
  selectedYear: string,
  selectedQuarter: string,
  type: "referral" | "user"
): { type: "referral" | "user"; month: number; year: number }[] {
  if (periodType === "month") {
    const month = Number(selectedMonth);
    const year = Number(selectedYear);
    return [{ type, month, year }];
  }
  const q = QUARTERS.find((x) => x.label === selectedQuarter);
  if (!q) return [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const quarterMonths = [
    { type, month: (q.quarter - 1) * 3 + 1, year: q.year },
    { type, month: (q.quarter - 1) * 3 + 2, year: q.year },
    { type, month: (q.quarter - 1) * 3 + 3, year: q.year },
  ];
  return quarterMonths.filter(
    (p) => p.year < currentYear || (p.year === currentYear && p.month < currentMonth)
  );
}

function exportReportCsv(
  periodLabel: string,
  summary: { totalIncome: number; expenses: number; grossProfit: number; grossMarginPct: number },
  referralRows: { name: string; email: string; total: number; lines: CommissionReport[] }[],
  userRows: { name: string; email: string; total: number; lines: CommissionReport[] }[]
): void {
  const lines: string[] = [];
  lines.push(`Commission Report - ${periodLabel}`);
  lines.push("");
  lines.push("Summary");
  lines.push(`Total Income,${summary.totalIncome.toFixed(2)}`);
  lines.push(`Expenses (COGS),${summary.expenses.toFixed(2)}`);
  lines.push(`Gross Profit,${summary.grossProfit.toFixed(2)}`);
  lines.push(`Gross Margin %,${summary.grossMarginPct.toFixed(1)}%`);
  lines.push("");
  lines.push("Referral Payouts");
  lines.push("Referral Name,Email,Total Commission,Month,Year,Line Commission");
  for (const row of referralRows) {
    if (row.lines.length === 0) {
      lines.push(`"${row.name.replace(/"/g, '""')}","${row.email.replace(/"/g, '""')}",${row.total.toFixed(2)},,,`);
    } else {
      row.lines.forEach((line, i) => {
        const name = i === 0 ? row.name.replace(/"/g, '""') : "";
        const email = i === 0 ? row.email.replace(/"/g, '""') : "";
        const total = i === 0 ? row.total.toFixed(2) : "";
        lines.push(`"${name}","${email}",${total},${line.month},${line.year},${(line.commission ?? 0).toFixed(2)}`);
      });
    }
  }
  lines.push("");
  lines.push("Internal Commissions");
  lines.push("User Name,Email,Total Commission,Month,Year,Line Commission");
  for (const row of userRows) {
    if (row.lines.length === 0) {
      lines.push(`"${row.name.replace(/"/g, '""')}","${row.email.replace(/"/g, '""')}",${row.total.toFixed(2)},,,`);
    } else {
      row.lines.forEach((line, i) => {
        const name = i === 0 ? row.name.replace(/"/g, '""') : "";
        const email = i === 0 ? row.email.replace(/"/g, '""') : "";
        const total = i === 0 ? row.total.toFixed(2) : "";
        lines.push(`"${name}","${email}",${total},${line.month},${line.year},${(line.commission ?? 0).toFixed(2)}`);
      });
    }
  }
  const csv = lines.join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `commission-report-${periodLabel.replace(/\s/g, "-")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const ReportsView: React.FC = () => {
  const { data: invoices = [] } = useGetCommissionInvoicesQuery();
  const { data: referrals = [] } = useGetCommissionReferralsQuery();
  const { data: users = [] } = useGetCommissionUsersQuery();
  const { data: pnlData = [] } = useGetCommissionPnlQuery();

  const [periodType, setPeriodType] = useState<"month" | "quarter">("quarter");
  const [selectedMonth, setSelectedMonth] = useState(() => String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [selectedQuarter, setSelectedQuarter] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const q = Math.ceil((now.getMonth() + 1) / 3);
    return `Q${q} ${y}`;
  });

  const periodLabel = useMemo(() => {
    if (periodType === "month") {
      const monthIndex = Number(selectedMonth) - 1;
      const monthName = new Date(2000, monthIndex, 1).toLocaleString("default", { month: "long" });
      return `${monthName} ${selectedYear}`;
    }
    return selectedQuarter;
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  const referralParamsList = useMemo(
    () => getReportParamsForPeriod(periodType, selectedMonth, selectedYear, selectedQuarter, "referral"),
    [periodType, selectedMonth, selectedYear, selectedQuarter]
  );
  const userParamsList = useMemo(
    () => getReportParamsForPeriod(periodType, selectedMonth, selectedYear, selectedQuarter, "user"),
    [periodType, selectedMonth, selectedYear, selectedQuarter]
  );

  const { data: refReports1 = [] } = useGetCommissionReportsQuery(
    referralParamsList[0] ?? { type: "referral" },
    { skip: referralParamsList.length === 0 }
  );
  const { data: refReports2 = [] } = useGetCommissionReportsQuery(referralParamsList[1] ?? referralParamsList[0] ?? { type: "referral" }, {
    skip: referralParamsList.length < 2,
  });
  const { data: refReports3 = [] } = useGetCommissionReportsQuery(referralParamsList[2] ?? referralParamsList[0] ?? { type: "referral" }, {
    skip: referralParamsList.length < 3,
  });

  const { data: userReports1 = [] } = useGetCommissionReportsQuery(
    userParamsList[0] ?? { type: "user" },
    { skip: userParamsList.length === 0 }
  );
  const { data: userReports2 = [] } = useGetCommissionReportsQuery(userParamsList[1] ?? userParamsList[0] ?? { type: "user" }, {
    skip: userParamsList.length < 2,
  });
  const { data: userReports3 = [] } = useGetCommissionReportsQuery(userParamsList[2] ?? userParamsList[0] ?? { type: "user" }, {
    skip: userParamsList.length < 3,
  });

  const allReferralReports = useMemo(() => {
    const sources = [refReports1, refReports2, refReports3].slice(0, referralParamsList.length);
    return sources.flat();
  }, [refReports1, refReports2, refReports3, referralParamsList.length]);

  const allUserReports = useMemo(() => {
    const sources = [userReports1, userReports2, userReports3].slice(0, userParamsList.length);
    return sources.flat();
  }, [userReports1, userReports2, userReports3, userParamsList.length]);

  const pnlInPeriod = useMemo(() => {
    if (periodType === "month") {
      const month = Number(selectedMonth);
      const year = Number(selectedYear);
      return pnlData.filter((r: GrossMarginRow) => r.month === month && r.year === year);
    }
    const q = QUARTERS.find((x) => x.label === selectedQuarter);
    if (!q) return [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    return pnlData.filter((r: GrossMarginRow) => {
      if (r.year !== q.year || Math.ceil(r.month / 3) !== q.quarter) return false;
      return r.year < currentYear || (r.year === currentYear && r.month < currentMonth);
    });
  }, [periodType, selectedMonth, selectedYear, selectedQuarter, pnlData]);

  const summary = useMemo(() => {
    const totalIncome = pnlInPeriod.reduce((s, r) => s + (r.totalIncome ?? 0), 0);
    const expenses = pnlInPeriod.reduce((s, r) => s + (r.cogs ?? 0), 0);
    const grossProfit = totalIncome - expenses;
    const grossMarginPct = totalIncome > 0 ? (grossProfit / totalIncome) * 100 : 0;
    return { totalIncome, expenses, grossProfit, grossMarginPct };
  }, [pnlInPeriod]);

  const referralReportRows = useMemo(() => {
    const byId = new Map<string, { referral: Referral; lines: CommissionReport[] }>();
    for (const ref of referrals) {
      byId.set(ref.id, { referral: ref, lines: [] });
    }
    for (const r of allReferralReports) {
      const id = r.referral ?? r.referralId ?? "";
      if (!id) continue;
      if (!byId.has(id)) byId.set(id, { referral: { id, organizationName: id, contactEmail: "", initialRatePercent: 0, recurringRatePercent: 0, initialRateByMonth: {}, recurringRateByMonth: {} } as Referral, lines: [] });
      byId.get(id)!.lines.push(r);
    }
    return Array.from(byId.values())
      .filter((x) => x.lines.length > 0)
      .map(({ referral, lines }) => ({
        name: referral.name ?? referral.organizationName ?? "—",
        email: referral.email ?? referral.contactEmail ?? "—",
        total: lines.reduce((s, l) => s + (l.commission ?? 0), 0),
        lines,
      }))
      .sort((a, b) => b.total - a.total);
  }, [referrals, allReferralReports]);

  const userReportRows = useMemo(() => {
    const byId = new Map<string, { name: string; email: string; lines: CommissionReport[] }>();
    for (const u of users) {
      byId.set(u.id, { name: u.name, email: u.email, lines: [] });
    }
    for (const r of allUserReports) {
      const id = r.user ?? r.userId ?? "";
      if (!id) continue;
      if (!byId.has(id)) byId.set(id, { name: id, email: "", lines: [] });
      byId.get(id)!.lines.push(r);
    }
    return Array.from(byId.values())
      .filter((x) => x.lines.length > 0)
      .map(({ name, email, lines }) => ({
        name,
        email,
        total: lines.reduce((s, l) => s + (l.commission ?? 0), 0),
        lines,
      }))
      .sort((a, b) => b.total - a.total);
  }, [users, allUserReports]);

  const handleExport = () => {
    exportReportCsv(periodLabel, summary, referralReportRows, userReportRows);
  };

  return (
    <div className="max-w-5xl mx-auto py-6 px-4">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h1 className="text-xl font-semibold text-gray-900 mb-4">Commission Report</h1>
          <div className="flex flex-wrap items-end gap-4">
            <BorderedSelect
              label="View by"
              options={PERIOD_TYPE_OPTIONS}
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as "month" | "quarter")}
            />
            {periodType === "month" ? (
              <>
                <BorderedSelect
                  label="Month"
                  options={MONTH_OPTIONS}
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                />
                <BorderedSelect
                  label="Year"
                  options={YEARS.map((y) => ({ value: String(y), label: String(y) }))}
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                />
              </>
            ) : (
              <BorderedSelect
                label="Quarter"
                options={QUARTERS.map((q) => ({ value: q.label, label: q.label }))}
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
              />
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2 ml-auto"
              onClick={handleExport}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        <div className="px-6 py-6 space-y-8">
          <section>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Summary — {periodLabel}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Total Income</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">{formatCurrency(summary.totalIncome)}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Expenses (COGS)</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">{formatCurrency(summary.expenses)}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Gross Profit</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">{formatCurrency(summary.grossProfit)}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Gross Margin %</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">{summary.grossMarginPct.toFixed(1)}%</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Referral Payouts — {periodLabel}
            </h2>
            <div className="rounded-md border border-gray-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-medium text-gray-700">Referral</TableHead>
                    <TableHead className="font-medium text-gray-700">Email</TableHead>
                    <TableHead className="font-medium text-gray-700 text-right">Total Commission</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referralReportRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                        No referral payouts for this period.
                      </TableCell>
                    </TableRow>
                  ) : (
                    referralReportRows.map((row) => (
                      <React.Fragment key={row.name + row.email}>
                        <TableRow className="bg-white">
                          <TableCell className="font-medium">{row.name}</TableCell>
                          <TableCell className="text-gray-600">{row.email}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(row.total)}</TableCell>
                        </TableRow>
                        {row.lines.map((line) => (
                          <TableRow key={line.id ?? `${line.month}-${line.year}`} className="bg-gray-50/50">
                            <TableCell className="pl-8 text-gray-500 text-sm">— Line</TableCell>
                            <TableCell className="text-gray-500 text-sm">
                              {line.month}/{line.year}
                            </TableCell>
                            <TableCell className="text-right text-gray-600 text-sm">{formatCurrency(Number((line.commission ?? 0).toFixed(2)))}</TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                  {referralReportRows.length > 0 && (
                    <TableRow className="bg-gray-100 border-t-2 border-gray-200 font-semibold">
                      <TableCell colSpan={2} className="font-semibold text-gray-900">
                        Total
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-900">
                        {formatCurrency(referralReportRows.reduce((s, r) => s + r.total, 0))}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Internal Commissions — {periodLabel}
            </h2>
            <div className="rounded-md border border-gray-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-medium text-gray-700">User</TableHead>
                    <TableHead className="font-medium text-gray-700">Email</TableHead>
                    <TableHead className="font-medium text-gray-700 text-right">Total Commission</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userReportRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                        No internal commissions for this period.
                      </TableCell>
                    </TableRow>
                  ) : (
                    userReportRows.map((row) => (
                      <React.Fragment key={row.name + row.email}>
                        <TableRow className="bg-white">
                          <TableCell className="font-medium">{row.name}</TableCell>
                          <TableCell className="text-gray-600">{row.email}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(row.total)}</TableCell>
                        </TableRow>
                        {row.lines.map((line) => (
                          <TableRow key={line.id ?? `${line.month}-${line.year}`} className="bg-gray-50/50">
                            <TableCell className="pl-8 text-gray-500 text-sm">— Line</TableCell>
                            <TableCell className="text-gray-500 text-sm">
                              {line.month}/{line.year}
                            </TableCell>
                            <TableCell className="text-right text-gray-600 text-sm">{formatCurrency(Number((line.commission ?? 0).toFixed(2)))}</TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                  {userReportRows.length > 0 && (
                    <TableRow className="bg-gray-100 border-t-2 border-gray-200 font-semibold">
                      <TableCell colSpan={2} className="font-semibold text-gray-900">
                        Total
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-900">
                        {formatCurrency(userReportRows.reduce((s, r) => s + r.total, 0))}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ReportsView;
